import ApiService from './Api.service';
import { searchCatalogTracks } from './AgentTrackSearch.service';
import { iTrack } from '../types/ITrack';
import { formatMusicalKey } from '../utils/formatMusicalKey';
import {
  normalizeCrateTrack,
  readArtistValue,
  readTrackArtist,
  readTrackTitle,
} from '../utils/normalizeCrateTrack';

export type AgentCrateTrackInput = {
  id?: string;
  artist?: string;
  title?: string;
  name?: string;
  Title?: string;
  Artist?: string;
  artists?: unknown;
  bpm?: number;
  BPM?: number;
  key?: string;
  Key_Camelot?: string;
  Key?: string;
  albumCover?: string;
};

const PLACEHOLDER_ID = /^agent-track-/i;

const readText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const trackTitle = (row: AgentCrateTrackInput): string =>
  readTrackTitle(row as Record<string, unknown>);

const trackArtist = (row: AgentCrateTrackInput): string =>
  readTrackArtist(row as Record<string, unknown>) ||
  readArtistValue(row.artists);

const trackId = (row: AgentCrateTrackInput): string => readText(row.id);

const isCatalogId = (id: string): boolean =>
  Boolean(id) && !PLACEHOLDER_ID.test(id);

const applyOverrides = (track: iTrack, row: AgentCrateTrackInput): iTrack =>
  normalizeCrateTrack({
    ...track,
    Artist: trackArtist(row) || track.Artist,
    Title: trackTitle(row) || track.Title,
    BPM: row.bpm ?? row.BPM ?? track.BPM,
    Key_Camelot:
      formatMusicalKey(row.key ?? row.Key_Camelot ?? row.Key) ||
      track.Key_Camelot,
    Key: formatMusicalKey(row.key ?? row.Key_Camelot ?? row.Key) || track.Key,
    albumCover: row.albumCover || track.AlbumCover,
  });

const fallbackTrack = (row: AgentCrateTrackInput): iTrack | null => {
  const normalized = normalizeCrateTrack({
    ID: isCatalogId(trackId(row)) ? trackId(row) : '',
    Artist: trackArtist(row),
    Title: trackTitle(row),
    BPM: row.bpm ?? row.BPM,
    Key_Camelot: formatMusicalKey(row.key ?? row.Key_Camelot ?? row.Key),
    Key: formatMusicalKey(row.key ?? row.Key_Camelot ?? row.Key),
    albumCover: row.albumCover,
  });
  return normalized.Title ? normalized : null;
};

const indexBulkTracks = (bulkRaw: Map<string, any>): Map<string, iTrack> => {
  const bulk = new Map<string, iTrack>();
  for (const [id, track] of bulkRaw.entries()) {
    const mapped = ApiService.mapBulkTrackToITrack(track);
    for (const key of [
      ...catalogIdAliases(id),
      ...catalogIdAliases(mapped.ID),
      ...catalogIdAliases(mapped.SpotifyID || ''),
    ]) {
      if (key && !bulk.has(key)) bulk.set(key, mapped);
    }
  }
  return bulk;
};

const resolveOne = async (
  row: AgentCrateTrackInput,
  bulk: Map<string, iTrack>,
): Promise<iTrack | null> => {
  const id = trackId(row);
  const fromBulk = id ? lookupBulkTrack(bulk, id) : undefined;
  if (fromBulk) {
    return applyOverrides(fromBulk, row);
  }

  if (isCatalogId(id)) {
    const hydrated = await ApiService.getTrackById(id);
    if (hydrated) return applyOverrides(hydrated, row);
  }

  const artist = trackArtist(row);
  const title = trackTitle(row);
  if (artist && title) {
    const hits = await searchCatalogTracks(`${artist} - ${title}`, 1);
    if (hits[0]?.id) {
      const hydrated = await ApiService.getTrackById(hits[0].id);
      if (hydrated) return applyOverrides(hydrated, row);
    }
  }

  return fallbackTrack(row);
};

const hydrateFromCatalog = (track: iTrack, hydrated: iTrack): iTrack =>
  normalizeCrateTrack({
    ...hydrated,
    ...track,
    Artist: track.Artist || hydrated.Artist,
    Title: track.Title || hydrated.Title,
    AlbumCover: track.AlbumCover || hydrated.AlbumCover,
    BPM: track.BPM ?? hydrated.BPM,
    Key_Camelot: track.Key_Camelot || hydrated.Key_Camelot,
    Key: track.Key || hydrated.Key,
    energy: track.energy ?? hydrated.energy,
    danceability: track.danceability ?? hydrated.danceability,
    valence: track.valence ?? hydrated.valence,
    popularity: track.popularity ?? hydrated.popularity,
    PreviewURL: track.PreviewURL || hydrated.PreviewURL,
  });

const catalogIdAliases = (id: string): string[] => {
  const trimmed = id.trim();
  if (!trimmed) return [];
  const withoutUri = trimmed.replace(/^spotify:track:/i, '');
  return [...new Set([trimmed, withoutUri])];
};

const isSpotifyTrackId = (id: string): boolean =>
  /^[0-9A-Za-z]{22}$/.test(id.trim().replace(/^spotify:track:/i, ''));

const lookupBulkTrack = (
  bulk: Map<string, iTrack> | undefined,
  id: string,
): iTrack | undefined => {
  if (!bulk) return undefined;
  for (const key of catalogIdAliases(id)) {
    const hit = bulk.get(key);
    if (hit) return hit;
  }
  return undefined;
};

const hydrateMissingMetadata = async (
  track: iTrack,
  bulk?: Map<string, iTrack>,
): Promise<iTrack> => {
  const normalized = normalizeCrateTrack(track);
  if (normalized.Artist && normalized.Title) return normalized;

  const id = normalized.SpotifyID || normalized.ID;
  if (!id || !isSpotifyTrackId(id)) return normalized;

  const fromBulk = lookupBulkTrack(bulk, id);
  if (fromBulk) {
    return hydrateFromCatalog(normalized, fromBulk);
  }

  const hydrated = await ApiService.getTrackById(id);
  if (!hydrated) return normalized;

  return hydrateFromCatalog(normalized, hydrated);
};

/** Fill in Artist/Title on crate rows that only have ids and audio metadata. */
export const hydrateCrateTracksMetadata = async (
  tracks: iTrack[],
): Promise<iTrack[]> => {
  const normalized = tracks.map((track) => normalizeCrateTrack(track));
  const needsHydration = normalized.some(
    (track) => !readTrackTitle(track) || !readTrackArtist(track),
  );
  if (!needsHydration) {
    return normalized;
  }

  const ids = [
    ...new Set(
      normalized
        .filter((track) => !readTrackTitle(track) || !readTrackArtist(track))
        .map((track) => track.SpotifyID || track.ID)
        .filter((id): id is string => Boolean(id) && isSpotifyTrackId(id)),
    ),
  ];

  const bulkRaw = ids.length ? await ApiService.getBulkTracks(ids) : new Map();
  const bulk = indexBulkTracks(bulkRaw);

  return Promise.all(
    normalized.map((track) => hydrateMissingMetadata(track, bulk)),
  );
};

/** Turn agent tool rows into full catalog iTracks before saving a My Crate. */
export const resolveAgentTracksForCrate = async (
  rows: AgentCrateTrackInput[],
): Promise<iTrack[]> => {
  const ids = [...new Set(rows.map(trackId).filter((id) => isCatalogId(id)))];
  const bulkRaw = ids.length ? await ApiService.getBulkTracks(ids) : new Map();
  const bulk = indexBulkTracks(bulkRaw);

  const resolved: iTrack[] = [];
  for (const row of rows) {
    const track = await resolveOne(row, bulk);
    if (!track) continue;
    const hydrated = await hydrateMissingMetadata(track);
    if (hydrated.Title) resolved.push(hydrated);
  }
  return resolved;
};
